// Wire-format types and API contract for Counseling Manual (상담매뉴얼, PRD 11).
// Mirrors the shape used by Management Guidelines (PRD 07) so adapter/error
// patterns stay consistent across the 참고 자료 관리 features.
//
// Model: 1 manual = 1 Grade (single `blocks` array, no multi-grade map). The
// Area/Sub-area/Item tree is READ-ONLY, sourced from PRO Item Design (SSoT).
// Classification Management (add/edit/delete) was removed — ADM.019/020,
// PRD 11 v1.2. Only the read cascade/filter derive from the tree.

export type ManualGrade = 'G1' | 'G2' | 'G3';

export const MANUAL_GRADES: readonly ManualGrade[] = ['G1', 'G2', 'G3'] as const;

export type ManualStatus = 'DRAFT' | 'PUBLISHED';

// PRD §4.3 — single block shape covering text/image/video. `text` carries
// rich-text HTML for `text` blocks; for `image`/`video` it's the description
// string (visible only when `showText` is true).
export type ContentBlockType = 'text' | 'image' | 'video';

export interface ContentBlock {
  id: number;
  type: ContentBlockType;
  title: string;
  text: string;
  showText: boolean;
  // Image/video: base64 dataUrl in the mock; planned swap to storage URL.
  dataUrl?: string;
  // Video only: external URL (YouTube etc). May coexist with `dataUrl`.
  url?: string;
}

// PRD §4.1 — list row shape rendered in the table.
export interface ConsultationManual {
  // 'C-###' (PRD §4.1) — system-assigned, never editable.
  cid: string;
  title: string;
  // null for a DRAFT saved without choosing a grade (PRD 11: no default).
  // Required (non-null) once PUBLISHED.
  grade: ManualGrade | null;
  // PRO-tree 3-tier path (대분류 / 소분류 / 항목). PRD §4.1.
  area: string;
  subArea: string;
  item: string;
  // Plain-text excerpt (first 200 chars, HTML stripped). PRD §4.3. '' when no
  // blocks — the table renders '-' in that case.
  preview: string;
  status: ManualStatus;
  // 'YYYY.MM.DD' (KST). Null while DRAFT. PRD §4.3.
  publishedAt: string | null;
}

// Detail returned when opening the edit panel — adds the block array and the
// serialized HTML snapshot. Split from `ConsultationManual` so the list
// response can stay light once BE wires the real endpoint.
export interface ConsultationManualDetail extends ConsultationManual {
  blocks: ContentBlock[];
  // Serialized read-only HTML of the block content (PRD §4.3). Derived on save.
  contentHtml: string;
  // SHCT-456 — last-modified timestamp from the DB (entity's audit updated_at),
  // 'YYYY.MM.DD' (KST). Shown as "최종 수정" in the preview. Null if the BE hasn't
  // populated it yet (the preview then falls back to today, as in create mode).
  updatedAt: string | null;
}

// PRD §3.4 / §4.2 — create payload. `preview` + `contentHtml` are derived FE-side
// from `blocks` on save and sent so the list can render without re-deriving.
export interface CreateManualInput {
  title: string;
  area: string;
  subArea: string;
  item: string;
  // null on a DRAFT with no grade chosen; publish-validated non-null.
  grade: ManualGrade | null;
  blocks: ContentBlock[];
  preview: string;
  contentHtml: string;
  // DRAFT for "임시저장", PUBLISHED for "게시".
  status: ManualStatus;
}

export type UpdateManualInput = Partial<CreateManualInput>;

// PRD §3.1 — server-side list query (C2, 2026-07-06). Area/sub-area/keyword
// filter + sort + pagination all run on the BE; the FE only sends params and
// renders the returned page. `sort` is a ManualSortKey string (kept loose to
// avoid a types→store import cycle); `direction` is asc|desc.
export interface ManualListParams {
  area?: string;
  subArea?: string;
  keyword?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  page: number;
  size: number;
}

export interface ManualListPage {
  items: ConsultationManual[];
  page: number;
  size: number;
  total: number;
}

// ── Category tree (영역 → 하위영역 → 항목) ──────────────────────────────────
// READ-ONLY, derived from PRO Item Design (SSoT). The cascading select +
// filters read from it (see utils/category-tree). Classification Management
// mutations were removed — ADM.019/020, PRD 11 v1.2.
export interface ManualCategorySubArea {
  name: string;
  items: string[];
}

export interface ManualCategoryArea {
  name: string;
  subAreas: ManualCategorySubArea[];
}

export interface ManualCategoryTree {
  areas: ManualCategoryArea[];
}

// SHCT-454 — image-block upload result. The image is uploaded to object storage
// (multipart) and only its URL is stored on the block, so the save payload no
// longer carries base64 (which inflated the JSON body past the request-size
// limit → 413). Mirrors Health Encyclopedia's UploadResult.
export interface UploadResult {
  url: string;
}

export type ManualApiErrorCode =
  | 'TITLE_REQUIRED'
  | 'TITLE_TOO_LONG'
  | 'AREA_REQUIRED'
  | 'SUB_AREA_REQUIRED'
  | 'ITEM_REQUIRED'
  | 'GRADE_REQUIRED'
  | 'CATEGORY_DUPLICATE'
  // SHCT-843 — 하나의 (항목, 단계) 조합에 게시 매뉴얼은 1건. Raised on PUBLISH only; 임시저장은
  // 판정하지 않는다. Carries `meta.cid` · `meta.title` naming the manual already holding the slot,
  // so the message can say WHICH one instead of only that the slot is taken.
  | 'MANUAL_DUPLICATE'
  | 'CATEGORY_NAME_REQUIRED'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  | 'UNKNOWN';

export class ManualApiError extends Error {
  readonly code: ManualApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: ManualApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'ManualApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, ManualApiError.prototype);
  }
}

export interface ConsultationManualApi {
  // PRD §3.1 — server-side list: filter (area/sub-area/keyword) + sort +
  // pagination happen on the BE (C2, 2026-07-06). Returns one page + total.
  list(params: ManualListParams): Promise<ManualListPage>;

  // PRD §3.5 — opens the edit panel. Returns blocks so the form hydrates
  // without an extra fetch.
  get(cid: string): Promise<ConsultationManualDetail>;

  create(input: CreateManualInput): Promise<ConsultationManualDetail>;

  // PRD §3.5 — PUBLISHED → PUBLISHED re-save refreshes publishedAt.
  update(cid: string, input: UpdateManualInput): Promise<ConsultationManualDetail>;

  // PRD §3.7 — hard delete (CONTENT_LIB:DELETE, Root-only).
  remove(cid: string): Promise<void>;

  // PRD §3.2 — the Area/Sub-area/Item tree backing the cascade + filters.
  // Read-only (PRO Item Design SSoT); no mutation methods — ADM.019/020.
  getCategoryTree(): Promise<ManualCategoryTree>;

  // PRD §3.6 / ADM.017 — upload an image block's file (JPG/PNG, ≤5MB) and get
  // back its storage URL (SHCT-454). BE: multipart POST to
  // `${consultationManuals}/uploads`.
  uploadImage(file: File): Promise<UploadResult>;
}
